package no.vareflow.app.data.local

import no.vareflow.app.data.json.Json
import no.vareflow.app.data.json.JsonArray
import no.vareflow.app.data.json.JsonObject
import no.vareflow.app.domain.*

/**
 * Platform-neutral version of the app's local storage format (the JSON kept under each
 * [StorageKeys] key). Phone (SharedPreferences) and browser (localStorage) store the same text.
 *
 * The format is a stable contract with data already on users' phones: keys, field names and
 * value shapes must not change. Every parser tolerates fields that older versions did not write
 * (missing department → "bar", missing VAT → 15 %, unknown enum names → a safe default).
 * Mirrors the Android org.json implementation field by field; the app's tests cross-check the two.
 */
object LocalCodec {
    fun usersToJson(users: List<UserAccount>): String = Json.write(users.map { user ->
        linkedMapOf(
            "id" to user.id,
            "name" to user.name,
            "username" to user.username,
            "password" to user.password,
            "role" to user.role.name,
            "permissions" to user.permissions.map { it.name },
            "accessUntil" to user.accessUntil,
            "active" to user.active,
            "categories" to user.allowedCategories.toList()
        )
    })

    fun productsToJson(products: List<Product>): String = Json.write(products.map { product ->
        linkedMapOf(
            "id" to product.id,
            "varenummer" to product.varenummer,
            "name" to product.name,
            "unit" to product.unit,
            "price" to product.pricePerUnit,
            "category" to product.category,
            "vat" to product.vat,
            "supplier" to product.supplier,
            "importStatus" to product.importStatus,
            "dataQuality" to product.dataQuality,
            "sourceRow" to product.sourceRow,
            "sourceCategory" to product.sourceCategory,
            "aliases" to product.aliases,
            "active" to product.active,
            "undergruppe" to product.undergruppe
        )
    })

    fun sessionsToJson(sessions: List<CountSession>): String = Json.write(sessions.map { session ->
        linkedMapOf(
            "id" to session.id,
            "inventorySessionId" to session.inventorySessionId,
            "companyId" to session.companyId,
            "locationId" to session.locationId,
            "locationName" to session.locationName,
            "yearMonth" to session.yearMonth,
            "userId" to session.userId,
            "userName" to session.userName,
            "createdAt" to session.createdAt,
            "syncState" to session.syncState.name,
            "serverAckAt" to session.serverAckAt,
            "syncError" to session.syncError,
            "lines" to session.lines.map { line ->
                linkedMapOf(
                    "productId" to line.productId,
                    "productName" to line.productName,
                    "unit" to line.unit,
                    "price" to line.pricePerUnit,
                    "vat" to line.vat,
                    "quantity" to line.quantity,
                    "departmentName" to line.departmentName,
                    "note" to line.note
                )
            }
        )
    })

    fun inventorySessionsToJson(inventorySessions: List<InventorySession>): String = Json.write(inventorySessions.map { session ->
        linkedMapOf(
            "id" to session.id,
            "companyId" to session.companyId,
            "locationId" to session.locationId,
            "locationName" to session.locationName,
            "yearMonth" to session.yearMonth,
            "status" to session.status.name,
            "createdAt" to session.createdAt,
            "closedAt" to session.closedAt,
            "snapshotVersion" to session.snapshotVersion
        )
    })

    fun categoryAssignmentsToJson(categoryAssignments: List<CategoryAssignment>): String = Json.write(categoryAssignments.map { assignment ->
        linkedMapOf(
            "sessionId" to assignment.sessionId,
            "category" to assignment.category,
            "assigneeIds" to assignment.assigneeIds.toList(),
            "status" to assignment.status.name,
            "updatedAt" to assignment.updatedAt
        )
    })

    fun departmentsToJson(departments: List<InventoryDepartment>): String = Json.write(departments.map { department ->
        linkedMapOf(
            "id" to department.id,
            "sessionId" to department.sessionId,
            "name" to department.name,
            "sortOrder" to department.sortOrder,
            "categories" to department.categories
        )
    })

    fun svinnToJson(records: List<SvinnRecord>): String = Json.write(records.map { r ->
        linkedMapOf(
            "id" to r.id, "yearMonth" to r.yearMonth, "sessionId" to r.sessionId, "productId" to r.productId,
            "productName" to r.productName, "category" to r.category, "unit" to r.unit, "pricePerUnit" to r.pricePerUnit,
            "quantity" to r.quantity, "reason" to r.reason.name, "note" to r.note, "userId" to r.userId,
            "userName" to r.userName, "createdAt" to r.createdAt, "updatedAt" to r.updatedAt
        )
    })

    fun parseSvinn(raw: String): List<SvinnRecord> {
        val array = Json.parseArray(raw)
        val records = mutableListOf<SvinnRecord>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            val productId = o.optString("productId")
            if (productId.isBlank()) return@repeat
            records += SvinnRecord(
                id = o.optString("id", randomId()), yearMonth = o.optString("yearMonth"),
                sessionId = o.optString("sessionId").takeIf { it.isNotBlank() && it != "null" },
                productId = productId, productName = o.optString("productName"), category = o.optString("category"),
                unit = o.optString("unit"), pricePerUnit = o.optDouble("pricePerUnit", 0.0), quantity = o.optDouble("quantity", 0.0),
                reason = SvinnReason.parse(o.optString("reason")), note = o.optString("note"), userId = o.optString("userId"),
                userName = o.optString("userName"), createdAt = o.optLong("createdAt", 0L), updatedAt = o.optLong("updatedAt", o.optLong("createdAt", 0L))
            )
        }
        return records
    }

    fun countEntriesToJson(countEntries: List<InventoryCountEntry>): String = Json.write(countEntries.map { entry ->
        linkedMapOf(
            "sessionId" to entry.sessionId,
            "productId" to entry.productId,
            "departmentId" to entry.departmentId,
            "quantity" to entry.quantity,
            "note" to entry.note,
            "updatedBy" to entry.updatedBy,
            "updatedAt" to entry.updatedAt,
            "version" to entry.version,
            "lastEventId" to entry.lastEventId,
            "pendingCloud" to entry.pendingCloud
        )
    })

    fun spotChecksToJson(spotChecks: List<SpotCheckRecord>): String = Json.write(spotChecks.map { record ->
        linkedMapOf(
            "id" to record.id,
            "sessionId" to record.sessionId,
            "productId" to record.productId,
            "productName" to record.productName,
            "expectedQty" to record.expectedQty,
            "checkedQty" to record.checkedQty,
            "checkerUserId" to record.checkerUserId,
            "checkerName" to record.checkerName,
            "createdAt" to record.createdAt,
            "matches" to record.matches
        )
    })

    fun auditEventsToJson(auditEvents: List<AuditEvent>): String = Json.write(auditEvents.map { event ->
        linkedMapOf(
            "id" to event.id,
            "timestamp" to event.timestamp,
            "actorUserId" to event.actorUserId,
            "actorName" to event.actorName,
            "action" to event.action,
            "entityType" to event.entityType,
            "entityId" to event.entityId,
            "field" to event.field,
            "beforeValue" to event.beforeValue,
            "afterValue" to event.afterValue,
            "source" to event.source,
            "deviceId" to event.deviceId,
            "appVersion" to event.appVersion,
            "serverTimestamp" to event.serverTimestamp,
            "companyId" to event.companyId,
            "inventorySessionId" to event.inventorySessionId,
            "category" to event.category,
            "productId" to event.productId
        )
    })

    fun productOrdersToJson(productOrderByCategory: Map<String, List<String>>): String = Json.write(productOrderByCategory)

    fun selectedDepartmentsToJson(selectedDepartmentBySession: Map<String, String>): String = Json.write(selectedDepartmentBySession)

    fun stringListToJson(values: List<String>): String = Json.write(values)

    // ---- Parsing. Each function takes the stored text; a malformed value throws JsonException. ----

    fun parseUsers(raw: String) = parseUsers(Json.parseArray(raw))
    fun parseProducts(raw: String) = parseProducts(Json.parseArray(raw))
    fun parseSessions(raw: String) = parseSessions(Json.parseArray(raw))
    fun parseInventorySessions(raw: String) = parseInventorySessions(Json.parseArray(raw))
    fun parseCategoryAssignments(raw: String) = parseCategoryAssignments(Json.parseArray(raw))
    fun parseDepartments(raw: String) = parseDepartments(Json.parseArray(raw))
    fun parseSelectedDepartments(raw: String) = parseSelectedDepartments(Json.parseObject(raw))
    fun parseCountEntries(raw: String) = parseCountEntries(Json.parseArray(raw))
    fun parseSpotChecks(raw: String) = parseSpotChecks(Json.parseArray(raw))
    fun parseAuditEvents(raw: String) = parseAuditEvents(Json.parseArray(raw))
    fun parseCategories(raw: String) = parseCategories(Json.parseArray(raw))
    fun parseCategoryOrder(raw: String) = parseCategoryOrder(Json.parseArray(raw))
    fun parseProductOrders(raw: String) = parseProductOrders(Json.parseObject(raw))

    fun parseUsers(array: JsonArray): List<UserAccount> {
        val users = mutableListOf<UserAccount>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            val categories = mutableSetOf<String>()
            val c = o.optArray("categories") ?: JsonArray(emptyList())
            repeat(c.length()) { categories += c.getString(it) }
            val role = runCatching { Role.valueOf(o.optString("role", Role.EMPLOYEE.name)) }.getOrDefault(Role.EMPLOYEE)
            val permissionArray = o.optArray("permissions")
            val permissions = if (permissionArray == null) {
                defaultPermissions(role)
            } else buildSet {
                repeat(permissionArray.length()) { j ->
                    runCatching { Permission.valueOf(permissionArray.getString(j)) }.getOrNull()?.let(::add)
                }
            }
            users += UserAccount(
                id = o.getString("id"),
                name = o.getString("name"),
                username = o.getString("username"),
                password = o.getString("password"),
                role = role,
                allowedCategories = categories,
                permissions = permissions,
                accessUntil = if (o.isNull("accessUntil")) null else o.getLong("accessUntil"),
                active = o.optBoolean("active", true)
            )
        }
        return users
    }

    fun parseProducts(array: JsonArray): List<Product> {
        val products = mutableListOf<Product>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            products += Product(
                id = o.getString("id"),
                varenummer = o.optString("varenummer"),
                name = o.getString("name"),
                unit = o.getString("unit"),
                pricePerUnit = o.optDouble("price", 0.0),
                category = o.getString("category"),
                vat = normalizeProductVat(o.optDouble("vat", 15.0)),
                supplier = o.optString("supplier"),
                importStatus = o.optString("importStatus", "Manuell"),
                dataQuality = o.optString("dataQuality"),
                sourceRow = if (o.isNull("sourceRow")) null else o.optInt("sourceRow"),
                sourceCategory = o.optString("sourceCategory"),
                aliases = buildList {
                    val aliasesArray = o.optArray("aliases") ?: JsonArray(emptyList())
                    repeat(aliasesArray.length()) { j -> add(aliasesArray.optString(j)) }
                },
                active = o.optBoolean("active", true),
                undergruppe = o.optString("undergruppe")
            )
        }
        return products
    }

    fun parseSessions(array: JsonArray): List<CountSession> {
        val sessions = mutableListOf<CountSession>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            val lines = mutableListOf<CountLineSnapshot>()
            val l = o.optArray("lines") ?: JsonArray(emptyList())
            repeat(l.length()) { j ->
                val line = l.getObject(j)
                lines += CountLineSnapshot(
                    productId = line.getString("productId"),
                    productName = line.getString("productName"),
                    unit = line.getString("unit"),
                    pricePerUnit = line.optDouble("price", 0.0),
                    vat = line.optDouble("vat", 0.0),
                    quantity = line.optDouble("quantity", 0.0),
                    departmentName = line.optString("departmentName", "Bar"),
                    note = line.optString("note")
                )
            }
            sessions += CountSession(
                id = o.getString("id"),
                inventorySessionId = if (o.isNull("inventorySessionId")) null else o.optString("inventorySessionId"),
                companyId = o.optString("companyId", DomainDefaults.DEMO_COMPANY_ID),
                locationId = o.optString("locationId", DomainDefaults.DEMO_LOCATION_ID),
                locationName = o.optString("locationName", "Hønefoss"),
                yearMonth = o.optString("yearMonth", currentYearMonth()),
                userId = o.getString("userId"),
                userName = o.getString("userName"),
                createdAt = o.getLong("createdAt"),
                lines = lines,
                syncState = runCatching {
                    CloudSaveState.valueOf(o.optString("syncState", CloudSaveState.LOCAL_SAVED.name))
                }.getOrDefault(CloudSaveState.LOCAL_SAVED),
                serverAckAt = if (o.isNull("serverAckAt")) null else o.optLong("serverAckAt"),
                syncError = if (o.isNull("syncError")) null else o.optString("syncError")
            )
        }
        return sessions
    }

    fun parseInventorySessions(array: JsonArray): List<InventorySession> {
        val inventorySessions = mutableListOf<InventorySession>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            inventorySessions += InventorySession(
                id = o.optString("id", randomId()),
                companyId = o.optString("companyId", DomainDefaults.DEMO_COMPANY_ID),
                locationId = o.optString("locationId", DomainDefaults.DEMO_LOCATION_ID),
                locationName = o.optString("locationName", "Hønefoss"),
                yearMonth = o.optString("yearMonth", currentYearMonth()),
                status = runCatching { InventoryStatus.valueOf(o.optString("status", InventoryStatus.DRAFT.name)) }
                    .getOrDefault(InventoryStatus.DRAFT),
                createdAt = o.optLong("createdAt", nowMillis()),
                closedAt = if (o.isNull("closedAt")) null else o.optLong("closedAt"),
                snapshotVersion = o.optInt("snapshotVersion", 0)
            )
        }
        return inventorySessions
    }

    fun parseCategoryAssignments(array: JsonArray): List<CategoryAssignment> {
        val categoryAssignments = mutableListOf<CategoryAssignment>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            val idsArray = o.optArray("assigneeIds") ?: JsonArray(emptyList())
            val ids = buildSet {
                repeat(idsArray.length()) { j ->
                    val id = idsArray.optString(j)
                    if (id.isNotBlank()) add(id)
                }
            }
            categoryAssignments += CategoryAssignment(
                sessionId = o.optString("sessionId"),
                category = o.optString("category"),
                assigneeIds = ids,
                status = runCatching { CategoryWorkStatus.valueOf(o.optString("status", CategoryWorkStatus.NOT_STARTED.name)) }
                    .getOrDefault(CategoryWorkStatus.NOT_STARTED),
                updatedAt = o.optLong("updatedAt", nowMillis())
            )
        }
        return categoryAssignments
    }

    fun parseDepartments(array: JsonArray): List<InventoryDepartment> {
        val departments = mutableListOf<InventoryDepartment>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            val sessionId = o.optString("sessionId")
            val name = o.optString("name")
            if (sessionId.isNotBlank() && name.isNotBlank()) {
                departments += InventoryDepartment(
                    id = o.optString("id", randomId()),
                    sessionId = sessionId,
                    name = name,
                    sortOrder = o.optInt("sortOrder", i),
                    categories = o.optArray("categories")?.let { c -> List(c.length()) { j -> c.getString(j) } }.orEmpty()
                )
            }
        }
        return departments
    }

    fun parseSelectedDepartments(obj: JsonObject): Map<String, String> {
        val selectedDepartmentBySession = mutableMapOf<String, String>()
        obj.keys().forEach { sessionId ->
            val departmentId = obj.optString(sessionId)
            if (departmentId.isNotBlank()) selectedDepartmentBySession[sessionId] = departmentId
        }
        return selectedDepartmentBySession
    }

    fun parseCountEntries(array: JsonArray): List<InventoryCountEntry> {
        val countEntries = mutableListOf<InventoryCountEntry>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            countEntries += InventoryCountEntry(
                sessionId = o.optString("sessionId"),
                productId = o.optString("productId"),
                departmentId = o.optString("departmentId", "bar"),
                quantity = if (o.isNull("quantity")) null else o.optDouble("quantity"),
                note = o.optString("note"),
                updatedBy = o.optString("updatedBy"),
                updatedAt = o.optLong("updatedAt", nowMillis()),
                version = o.optInt("version", 0),
                lastEventId = if (o.isNull("lastEventId")) null else o.optString("lastEventId"),
                pendingCloud = o.optBoolean("pendingCloud", false)
            )
        }
        return countEntries
    }

    fun parseSpotChecks(array: JsonArray): List<SpotCheckRecord> {
        val spotChecks = mutableListOf<SpotCheckRecord>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            spotChecks += SpotCheckRecord(
                id = o.optString("id", randomId()),
                sessionId = if (o.isNull("sessionId")) null else o.optString("sessionId"),
                productId = o.optString("productId"),
                productName = o.optString("productName"),
                expectedQty = o.optDouble("expectedQty"),
                checkedQty = o.optDouble("checkedQty"),
                checkerUserId = o.optString("checkerUserId"),
                checkerName = o.optString("checkerName"),
                createdAt = o.optLong("createdAt", nowMillis()),
                matches = o.optBoolean("matches")
            )
        }
        return spotChecks
    }

    fun parseAuditEvents(array: JsonArray): List<AuditEvent> {
        val auditEvents = mutableListOf<AuditEvent>()
        repeat(array.length()) { i ->
            val o = array.getObject(i)
            auditEvents += AuditEvent(
                id = o.optString("id", randomId()),
                timestamp = o.optLong("timestamp", nowMillis()),
                actorUserId = o.optString("actorUserId"),
                actorName = o.optString("actorName", "Ukjent bruker"),
                action = o.optString("action"),
                entityType = o.optString("entityType"),
                entityId = o.optString("entityId"),
                field = o.optString("field"),
                beforeValue = o.optString("beforeValue"),
                afterValue = o.optString("afterValue"),
                source = o.optString("source", "app"),
                deviceId = o.optString("deviceId"),
                appVersion = o.optString("appVersion", "0.8.18"),
                serverTimestamp = if (o.isNull("serverTimestamp")) null else o.optLong("serverTimestamp"),
                companyId = o.optString("companyId", DomainDefaults.DEMO_COMPANY_ID),
                inventorySessionId = if (o.isNull("inventorySessionId")) null else o.optString("inventorySessionId"),
                category = if (o.isNull("category")) null else o.optString("category"),
                productId = if (o.isNull("productId")) null else o.optString("productId")
            )
        }
        return auditEvents
    }

    fun parseCategories(array: JsonArray): List<String> {
        val categories = mutableListOf<String>()
        repeat(array.length()) { i ->
            val category = array.optString(i).trim()
            if (category.isNotBlank() && categories.none { it.equals(category, true) }) categories += category
        }
        return categories
    }

    fun parseCategoryOrder(array: JsonArray): List<String> {
        val categoryOrder = mutableListOf<String>()
        repeat(array.length()) { i ->
            val category = array.optString(i).trim()
            if (category.isNotBlank()) categoryOrder += category
        }
        return categoryOrder
    }

    fun parseProductOrders(obj: JsonObject): Map<String, List<String>> {
        val productOrderByCategory = mutableMapOf<String, List<String>>()
        for (category in obj.keys()) {
            val array = obj.optArray(category) ?: continue
            val ids = buildList {
                repeat(array.length()) { i ->
                    val id = array.optString(i)
                    if (id.isNotBlank()) add(id)
                }
            }
            productOrderByCategory[category] = ids
        }
        return productOrderByCategory
    }
}
